import { Link } from 'react-router-dom';
import { useTheme } from '@/context/ThemeContext';
import { Sun, Moon, ArrowLeft, Target, Users, Shield, Globe, Zap, Award, BarChart3, Briefcase } from 'lucide-react';
import { MetaTradifyLogo, MetaTradifyLogoSmall } from '@/components/MetaTradifyLogo';

const stats = [
  { value: '10,000+', label: 'Active Traders' },
  { value: '$500M+', label: 'Trading Volume' },
  { value: '50+', label: 'Markets Available' },
  { value: '99.9%', label: 'Uptime SLA' },
];

const values = [
  { icon: Shield, title: 'Security First', desc: 'Bank-grade encryption and multi-factor authentication protect your assets and data at every level.' },
  { icon: Zap, title: 'Speed & Reliability', desc: 'Sub-second order execution with 99.9% platform uptime, powered by distributed infrastructure.' },
  { icon: Globe, title: 'Global Access', desc: 'Trade crypto, forex, indices, and commodities — all from a single platform, available 24/7.' },
  { icon: Users, title: 'Community Driven', desc: 'Built with continuous trader feedback. Every feature is designed to solve real trading challenges.' },
];

const team = [
  { name: 'Arjun Mehta', role: 'Founder & CEO', desc: 'Ex-Goldman Sachs. 12+ years in fintech and quantitative trading.' },
  { name: 'Priya Sharma', role: 'CTO', desc: 'Former lead engineer at Binance. Expert in high-frequency trading systems.' },
  { name: 'Rahul Verma', role: 'Head of Product', desc: 'Built trading products at Zerodha. Passionate about trader UX.' },
  { name: 'Ananya Gupta', role: 'Head of Compliance', desc: 'SEBI-certified. Ensures regulatory compliance across all operations.' },
];

export default function AboutPage() {
  const { theme, toggleTheme } = useTheme();

  return (
    <div className="min-h-screen bg-[var(--bg-primary)] text-[var(--text-primary)]">
      {/* Nav */}
      <nav className="fixed top-0 w-full z-50 glass border-b border-[var(--border-subtle)]" data-testid="about-nav">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <MetaTradifyLogo size={32} />
            <span className="text-lg font-bold font-['Outfit'] tracking-tight">metaTradify</span>
          </Link>
          <div className="flex items-center gap-3">
            <button onClick={toggleTheme} className="p-2 rounded-lg bg-[var(--hover-bg)] border border-[var(--border-subtle)] transition-all">
              {theme === 'dark' ? <Sun className="w-4 h-4 text-amber-400" /> : <Moon className="w-4 h-4 text-green-500" />}
            </button>
            <Link to="/" className="flex items-center gap-1.5 px-4 py-2 text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors">
              <ArrowLeft className="w-4 h-4" /> Back
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative pt-28 pb-16 px-6">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/3 right-1/4 w-80 h-80 bg-green-500/8 rounded-full blur-[120px]" />
        </div>
        <div className="max-w-7xl mx-auto relative">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[var(--border-subtle)] bg-[var(--hover-bg)] text-xs text-[var(--text-secondary)] mb-6">
              <Briefcase className="w-3 h-3 text-green-400" /> Our Story
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold font-['Outfit'] tracking-tight leading-[1.1] mb-5" data-testid="about-heading">
              About <span className="text-green-400">metaTradify</span>
            </h1>
            <p className="text-base text-[var(--text-secondary)] leading-relaxed max-w-xl">
              We're on a mission to democratize professional trading. metaTradify combines institutional-grade tools with intuitive design, making sophisticated trading accessible to everyone.
            </p>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="px-6 pb-16">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {stats.map((s, i) => (
              <div key={i} className="p-6 bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded-lg text-center" data-testid={`about-stat-${i}`}>
                <p className="text-2xl md:text-3xl font-bold font-mono text-green-400">{s.value}</p>
                <p className="text-xs text-[var(--text-secondary)] mt-1">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-base font-semibold font-['Outfit'] text-green-400 mb-2">Our Mission</h2>
              <p className="text-2xl md:text-3xl font-bold font-['Outfit'] tracking-tight mb-5">
                Empowering traders with technology
              </p>
              <div className="space-y-4 text-sm text-[var(--text-secondary)] leading-relaxed">
                <p>
                  Founded in 2024, metaTradify was born from a simple observation: professional trading tools were either too expensive for individual traders or too complicated to use effectively.
                </p>
                <p>
                  Our platform bridges this gap by combining real-time market data, AI-powered analytics, and an intuitive interface that puts the power of institutional trading in every trader's hands.
                </p>
                <p>
                  We believe that access to quality trading tools should not be determined by the size of your portfolio. Whether you're trading your first $100 or managing millions, metaTradify gives you the same professional-grade experience.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { icon: Target, label: 'Precision', value: 'Real-time execution' },
                { icon: BarChart3, label: 'Analytics', value: 'AI-powered insights' },
                { icon: Shield, label: 'Security', value: 'Bank-grade encryption' },
                { icon: Award, label: 'Excellence', value: 'Award-winning UX' },
              ].map((item, i) => {
                const Icon = item.icon;
                return (
                  <div key={i} className="p-5 bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded-lg">
                    <Icon className="w-6 h-6 text-green-400 mb-3" />
                    <p className="text-sm font-semibold text-[var(--text-primary)]">{item.label}</p>
                    <p className="text-xs text-[var(--text-secondary)] mt-0.5">{item.value}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="mb-12">
            <h2 className="text-base font-semibold font-['Outfit'] text-green-400 mb-2">Our Values</h2>
            <p className="text-2xl md:text-3xl font-bold font-['Outfit'] tracking-tight">What drives us</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {values.map((v, i) => {
              const Icon = v.icon;
              return (
                <div key={i} className="p-6 bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded-lg hover:border-green-500/20 transition-colors group" data-testid={`about-value-${i}`}>
                  <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center mb-4 group-hover:bg-green-500/20 transition-colors">
                    <Icon className="w-5 h-5 text-green-400" />
                  </div>
                  <h3 className="text-sm font-semibold font-['Outfit'] mb-2">{v.title}</h3>
                  <p className="text-xs text-[var(--text-secondary)] leading-relaxed">{v.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Team */}
      {/* <section className="py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="mb-12">
            <h2 className="text-base font-semibold font-['Outfit'] text-green-400 mb-2">Leadership</h2>
            <p className="text-2xl md:text-3xl font-bold font-['Outfit'] tracking-tight">Meet our team</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {team.map((t, i) => (
              <div key={i} className="p-6 bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded-lg" data-testid={`about-team-${i}`}>
                <div className="w-12 h-12 rounded-full bg-green-500/15 flex items-center justify-center mb-4">
                  <span className="text-lg font-bold text-green-400">{t.name.split(' ').map(n => n[0]).join('')}</span>
                </div>
                <h3 className="text-sm font-semibold font-['Outfit']">{t.name}</h3>
                <p className="text-xs text-green-400 mb-2">{t.role}</p>
                <p className="text-xs text-[var(--text-secondary)] leading-relaxed">{t.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section> */}

      {/* CTA */}
      <section className="py-16 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-bold font-['Outfit'] tracking-tight mb-4">Join the metaTradify community</h2>
          <p className="text-sm text-[var(--text-secondary)] mb-8">Start your trading journey with a free demo account. No credit card required.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/login-dashboard" data-testid="about-cta-register" className="inline-flex items-center gap-2 px-6 py-3 bg-green-500 hover:bg-green-600 text-white rounded text-sm font-medium transition-all hover:shadow-lg hover:shadow-green-500/25">
              Get Started Free
            </Link>
            <Link to="/contact" className="inline-flex items-center gap-2 px-6 py-3 border border-[var(--border-subtle)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] rounded text-sm transition-all">
              Contact Us
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[var(--border-subtle)] py-8 px-6">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <MetaTradifyLogoSmall size={24} />
            <span className="text-sm font-semibold font-['Outfit']">metaTradify</span>
          </div>
          <div className="flex items-center gap-6 text-xs text-[var(--text-tertiary)]">
            <Link to="/about" className="hover:text-[var(--text-secondary)] transition-colors">About</Link>
            <Link to="/contact" className="hover:text-[var(--text-secondary)] transition-colors">Contact</Link>
            <Link to="/privacy-policy" className="hover:text-[var(--text-secondary)] transition-colors">Privacy</Link>
          </div>
          <p className="text-xs text-[var(--text-tertiary)]">&copy; 2025 metaTradify. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
